import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { flatten, sortBy, uniq } from '@/lib/array'
import Popup from '@/components/Popup'
import FontIcon from '@/components/icons/FontIcon'


const fontWeights = "Thin ExtraLight Light Regular Medium SemiBold Bold ExtraBold Black".split(' ')

export default function FontPicker({fonts, uid, size, kerning, spacing, onChange, onOpen, onClose, button}) {

  const currentFont = fonts.find(font => font.uid === uid)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (visible) {
      onOpen && onOpen()
    } else {
      onClose && onClose()
    }
  }, [visible])

  if (!currentFont) return button || null


  return <>
    <div className='relative full-width'>
      <div onClick={() => setVisible(v => !v)}>
        { button ?
          button
        :
          <button className='toolbar-button button px-0' >
            <FontIcon/>
          </button>
        }
      </div>

      { !button &&
        <div className='hover-parent z-3 pointer-events-none absolute mt-1'>
          <div className='py-1 px-3 text-sm bold white bg-black-transparent rounded nowrap'>
            Font
          </div>
        </div>
      }

      <Popup visible={visible} onClickOutside={() => setVisible(false)}>
        <div className='flex-col gap-2 p-3 min-width-300'>
          <FontUI
            fonts={fonts}
            uid={uid}
            size={size}
            kerning={kerning}
            spacing={spacing}
            onChange={onChange}
            autoHideWhileSizing
            visible={visible}
          />
        </div>
      </Popup>
    </div>
  </>
}



export function FontUI({fonts, uid, size, kerning, spacing, onChange, autoHideWhileSizing, visible}) {
  const currentFont = fonts.find(font => font.uid === uid)

  const indexOfRegularWeight = fontWeights.indexOf('Regular')

  const [weightIndex, setWeightIndex] = useState(indexOfRegularWeight)
  const [changingFontSize, setChangingFontSize] = useState(false)
  const [spacingRatio, setSpacingRatio] = useState((kerning || 0) / (size || 40))
  const [spacingUIVisible, setSpacingUIVisible] = useState(false)


  useEffect(() => {
    if (!currentFont) return
  }, [weightIndex])


  useEffect(() => {
    const currentWeightIndex = weightIndexOf(currentFont)
    const defaultWeightIndex = Number.isInteger(currentWeightIndex) ? currentWeightIndex : indexOfRegularWeight
    setWeightIndex(defaultWeightIndex)
  }, [currentFont])


  useEffect(() => {
    if (!visible) return

    const currentFontElement = document.getElementsByClassName('selected-font')[0]
    if (!currentFontElement) return

    currentFontElement.scrollIntoView({block: 'center'})

    // Make sure scrollIntoView doesn't scroll image editor tools horizontally
    const toolsPanel = document.getElementById('image-editor-tools')
    if (toolsPanel) toolsPanel.scrollLeft = 0
  }, [visible])


  const families = uniq(fonts.map(font => font.family))
  const fontsInSameFamilyAndStyle = currentFont ? fontsIn(currentFont.family).filter(font => isItalic(font) === isItalic(currentFont)) : []

  const fontsToShow = flatten(families.map(fam => {
    const nonItalicFonts = fontsIn(fam).filter(font => !isItalic(font))
    const italicFonts = fontsIn(fam).filter(isItalic)

    const matches = [
      closestFontByWeightIndex(nonItalicFonts, weightIndex),
      closestFontByWeightIndex(italicFonts, weightIndex),
    ].filter(Boolean)

    return sortBy(matches, niceName)
  })).filter(Boolean)


  function fontsIn(fam) {
    if (!fam) return []
    return fonts.filter(font => font.family === fam)
  }

  function isItalic(font)  {
    if (!font) return false
    return font.name.endsWith('Italic')
  }

  function weightOf(font) {
    if (!font) return ''
    const fontStyle = font.name.split('-')[1]
    if (!fontStyle) return 'Regular'
    return fontStyle.replace('Italic', '').trim() || 'Regular'
  }

  function weightIndexOf(font) {
    if (!font) return 0
    return fontWeights.indexOf(weightOf(font))
  }

  function closestFontByWeightIndex(fontList, index) {
    const list = sortBy(fontList, weightIndexOf)
    if (index >= indexOfRegularWeight) {
      return list.reverse().find(font => weightIndexOf(font) <= index)
    } else {
      return list.find(font => weightIndexOf(font) >= index)
    }
  }

  function handleWeightChange(e) {
    const newIndex = parseInt(e.target.value, 10)
    if (isNaN(newIndex)) return
    setWeightIndex(newIndex)

    if (!currentFont) return

    const newFont = closestFontByWeightIndex(fontsInSameFamilyAndStyle, newIndex)
    if (newFont) {
      onChange({font: newFont.uid})
    }
  }

  function handleChangeFont(e) {
    const newUid = e.currentTarget.value
    onChange({font: newUid})
  }


  function handleKerningChange(e) {
    let newKerning = e.target.value
    newKerning = parseInt(newKerning, 10)
    if (isNaN(newKerning)) newKerning = 0
    onChange({kerning: newKerning})
  }

  function handleSpacingChange(e) {
    let newSpacing = e.target.value
    newSpacing = parseInt(newSpacing, 10)
    if (isNaN(newSpacing)) newSpacing = 0
    setSpacingRatio(newSpacing / size)
    onChange({spacing: newSpacing})
  }

  function handleFontSizeChange(e) {
    let newSize = e.target.value
    newSize = parseInt(newSize, 10)
    if (isNaN(newSize)) newSize = size
    if (newSize < 1) newSize = 1

    // Need to adjust the spacing to keep the same ratio with the new font size
    onChange({size: newSize, spacing: Math.round(newSize * spacingRatio)})
  }

  function niceName(font) {
    if (!font) return ''
    let name = font.family
    if (isItalic(font)) name += ' Italic'
    return name
  }

  return <>
    <div className='flex-col full-width overflow-hidden max-height-full'>

      <div className='hidden'>
        <div className='flex-row spread'>
          <label>Size</label>
          <div className='gray text-sm'>
            <span>{size}px</span>
          </div>
        </div>

        <input
          className='full-width'
          type="range"
          min="40"
          max="400"
          value={size || 40}
          onChange={handleFontSizeChange}
          onPointerDown={() => setChangingFontSize(true)}
          onPointerUp={() => setChangingFontSize(false)}
        />
      </div>

      <div className={classNames('flex-col overflow-hidden', {hidden: (changingFontSize && autoHideWhileSizing)})}>

        { fontWeights.length > 0 &&
          <div>
            <div className='flex-row spread'>
              <label>Weight</label>
              <div className='gray text-sm'>
                { fontsInSameFamilyAndStyle.length <= 1 ?
                  <span>Unavailable for current font</span>
                :
                  <span>{fontWeights[weightIndex]}</span>
                }
              </div>
            </div>
            <input
              className='full-width'
              type="range"
              min="0"
              max={fontWeights.length - 1}
              value={weightIndex}
              onChange={handleWeightChange}
            />
          </div>
        }

        <div className='relative my-2 flex-grow overflow-hidden flex-col' style={{maxHeight: '50vh'}}>
          <div className='rounded z-1 absolute-full shadow-inset-xl pointer-events-none'></div>
          { fontsToShow.length === 0 ?
            <div className='flex-row flex-align-center justify-center p-2 gray border border-gray rounded'>
              No fonts found
            </div>
          :

            <div className='rounded flex-col relative border bg-white overflow-auto-y'>
              {fontsToShow.map(font => {
                const isSelected = niceName(font) === niceName(currentFont)
                return (
                  <button
                    key={font.uid}
                    onClick={() => handleChangeFont({currentTarget: {value: font.uid}})}
                    style={{fontFamily: font.uid, marginTop: '-1px', fontSize: '1.2rem', minHeight: '2.5rem', width: '100%', maxWidth: 280}}
                    className={classNames(
                      'default-text-color text-left flex-row flex-align-center nowrap p-2 border-gray hover_bg-light-blue overflow-hidden',
                      {'bg-light-blue': isSelected, 'bg-white': !isSelected, 'selected-font': isSelected},
                    )}
                    title={niceName(font)}
                  >
                    {niceName(font)}
                  </button>
                )
              })}
            </div>

          }
        </div>

        { spacingUIVisible ?
          <div className="flex-row gap-5">
            <div>
              <div className='flex-row spread'>
                <label>Character</label>
                <div className='gray text-sm'>
                  <span>{kerning}px</span>
                </div>
              </div>

              <input
                className='full-width'
                type="range"
                min={-1 * Math.round(size / 3)}
                max={Math.round(size / 3)}
                value={kerning}
                onChange={handleKerningChange}
              />
            </div>

            <div>
              <div className='flex-row spread'>
                <label>Line</label>
                <div className='gray text-sm'>
                  <span>{spacing}px</span>
                </div>
              </div>
              <input
                className='full-width'
                type="range"
                min={-1 * size}
                max={size}
                value={spacing || 0}
                onChange={handleSpacingChange}
              />
            </div>
          </div>
        :
          <div className="flex-row gap-5">
            <button className="button-link text-sm hover_underline text-center full-width" onClick={() => setSpacingUIVisible(v => !v)}>
              Adjust character and line spacing
            </button>
          </div>
        }

      </div>

    </div>
  </>
}
